// Historial de un elemento en su editor: fecha, evento, detalle y quién lo hizo. Cada dominio
// traduce su bitácora a estas filas.
import type { ReactNode } from 'react';
import { Button, Skeleton } from '@/kit';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { SectionTitle } from '@/shared/patterns/EditorForm';
import { LifecycleEventBadge } from './LifecycleBadges';
import type { LifecycleEvent } from './lifecycleStatus';

export interface LifecycleHistoryEntry {
    id: string;
    /** ISO; `null` si la bitácora no trae fecha. */
    at: string | null;
    event: LifecycleEvent | null;
    /** Etiqueta del evento cuando el diccionario no lo conoce. */
    eventLabel?: string;
    detail?: ReactNode;
    actor?: string;
    /** Debajo de la fila, a todo el ancho: lo que cambió en el evento. */
    below?: ReactNode;
}

export interface LifecycleHistoryProps {
    title: string;
    entries: readonly LifecycleHistoryEntry[];
    isLoading: boolean;
    /** Mensaje de error; con él la lista no se pinta. */
    error?: string | null;
    onRetry?: () => void;
    emptyText: ReactNode;
    /** Va dentro del recuadro, al pie: cargar más, exportar. */
    footer?: ReactNode;
    /** Va bajo el recuadro: qué tanto cubre la lista. */
    note?: ReactNode;
}

export function LifecycleHistory({
    title,
    entries,
    isLoading,
    error,
    onRetry,
    emptyText,
    footer,
    note,
}: LifecycleHistoryProps) {
    return (
        <section aria-label={title} className="mb-4">
            <SectionTitle>{title}</SectionTitle>
            <div className="overflow-hidden rounded-md border border-border bg-card">
                {renderBody()}
                {footer ? (
                    <div className="flex items-center justify-end gap-1 border-t border-border/40 px-1.5 py-1">
                        {footer}
                    </div>
                ) : null}
            </div>
            {note ? <p className="mt-1 text-[10px] text-muted-foreground">{note}</p> : null}
        </section>
    );

    function renderBody() {
        if (isLoading) return <Skeleton lines={2} className="m-2.5" />;
        if (error) {
            return (
                <div className="flex items-center justify-between gap-2 px-2.5 py-2 text-[10.5px] text-destructive">
                    <span>{error}</span>
                    {onRetry ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10.5px]"
                            onClick={onRetry}
                        >
                            Reintentar
                        </Button>
                    ) : null}
                </div>
            );
        }
        if (entries.length === 0) {
            return (
                <p className="px-2.5 py-3 text-center text-[10.5px] italic text-muted-foreground">
                    {emptyText}
                </p>
            );
        }
        return (
            <ul>
                {entries.map((entry) => (
                    <li
                        key={entry.id}
                        className="grid grid-cols-[110px_auto_minmax(0,1fr)] items-center gap-2 border-b border-border/40 px-2.5 py-1.5 text-[10.5px] last:border-b-0"
                    >
                        <span className="tabular-nums text-muted-foreground">
                            {entry.at ? formatDateTimeMx(entry.at) : '—'}
                        </span>
                        <LifecycleEventBadge event={entry.event} label={entry.eventLabel} />
                        <span
                            className="min-w-0 truncate text-foreground"
                            title={entryTitle(entry)}
                        >
                            {entry.detail}
                            {entry.actor ? (
                                <span className="text-muted-foreground">
                                    {entry.detail ? ' · ' : ''}
                                    {entry.actor}
                                </span>
                            ) : null}
                        </span>
                        {entry.below ? (
                            <div className="col-span-3 text-muted-foreground">{entry.below}</div>
                        ) : null}
                    </li>
                ))}
            </ul>
        );
    }
}

/** El texto completo de la fila para el tooltip; el detalle se corta a una línea. */
function entryTitle(entry: LifecycleHistoryEntry): string | undefined {
    const parts = [typeof entry.detail === 'string' ? entry.detail : '', entry.actor ?? ''];
    const text = parts.filter((part) => part !== '').join(' · ');
    return text === '' ? undefined : text;
}
